import React from "react";
import { Field, ConfigInput } from "./Field";

/**
 * Email destination configuration.
 *
 * Iter-61: the state allowlist + per-state fees now live at the parent
 * form level so every destination type gets the shared checkbox UI
 * (`StateAllowlistFields`). Backend keys `config.state_allowlist` +
 * `config.state_fees` / `config.state_fees_text` are unchanged; the
 * shared component is fully backward-compatible with legacy
 * comma-separated string values.
 */
export default function EmailFields({ config, setConfig, extraPlace = false }) {
  return (
    <>
      <Field label="Recipient email" required colSpan={2}>
        <ConfigInput name="to" config={config} setConfig={setConfig} type="email"
          testId="dest-to" required mono={false} />
      </Field>
      <Field label="Subject template" colSpan={2}>
        <ConfigInput name="subject" config={config} setConfig={setConfig}
          placeholder="Defaults to “New qualified lead — First Last (STATE)”" mono={false} />
      </Field>
      <Field label="Template" colSpan={2}>
        <select
          data-testid="dest-email-template"
          className="w-full border border-black px-3 py-2 mt-1"
          value={config.template || ""}
          onChange={(e) => setConfig("template", e.target.value)}
        >
          <option value="">Default (buyer-friendly 4-section table)</option>
          <option value="vance">Alabama PI field layout (buyer-specific field names)</option>
        </select>
      </Field>
      {!extraPlace && <MainOnlyEmailFields config={config} setConfig={setConfig} />}
    </>
  );
}

// Digest, payment and invoice settings belong to the main delivery only.
function MainOnlyEmailFields({ config, setConfig }) {
  return (
    <>
      <Field label="Daily digest email" colSpan={2}>
        <label className="flex items-center gap-2 mt-1 cursor-pointer">
          <input
            type="checkbox"
            data-testid="dest-daily-digest"
            checked={!!config.daily_digest_enabled}
            onChange={(e) => setConfig("daily_digest_enabled", e.target.checked)}
            className="border border-black w-4 h-4"
          />
          <span className="text-xs">
            Send this buyer a daily receipt at 08:00 UTC listing every lead delivered in the past 24 hours.
          </span>
        </label>
      </Field>
      <Field label="Digest recipient (optional — defaults to the lead email address)" colSpan={2}>
        <ConfigInput name="daily_digest_recipient" config={config} setConfig={setConfig}
          type="email" testId="dest-digest-recipient" mono={false}
          placeholder="ops@buyer.com" />
      </Field>
      {/* Iter-30: Payment mode + optional weekly invoice for postpay buyers. */}
      <Field label="Payment mode" colSpan={2}>
        <select
          data-testid="dest-payment-mode"
          className="w-full border border-black px-3 py-2 mt-1"
          value={config.payment_mode || "prepay"}
          onChange={(e) => setConfig("payment_mode", e.target.value)}
        >
          <option value="prepay">Prepay (buyer has funded account — no invoice)</option>
          <option value="postpay">Postpay (buyer pays after — receives weekly invoice)</option>
        </select>
      </Field>
      {(config.payment_mode === "postpay") && (
        <Field label="Weekly invoice email" colSpan={2}>
          <label className="flex items-center gap-2 mt-1 cursor-pointer">
            <input
              type="checkbox"
              data-testid="dest-invoice-enabled"
              checked={!!config.invoice_enabled}
              onChange={(e) => setConfig("invoice_enabled", e.target.checked)}
              className="border border-black w-4 h-4"
            />
            <span className="text-xs">
              Send this buyer a Monday-morning invoice at 09:00 UTC with a
              per-state breakdown of leads delivered in the past 7 days.
            </span>
          </label>
          <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
            Requires per-state fees to be set above. Admin can turn this off any time.
          </div>
        </Field>
      )}
    </>
  );
}
